import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, BadgePercent, Eye, Wallet, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HOME } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTOPLAY_MS = 6000;
const HERO_IMAGE =
  'https://customer-assets-m6fa6gv7.emergentagent.net/job_dress-gallery-87/artifacts/0ztnhr4d_%D8%AA%D8%B5%D9%85%D9%8A%D9%85%20%D8%A8%D8%AF%D9%88%D9%86%20%D8%B9%D9%86%D9%88%D8%A7%D9%86%20%2812%29.png';

// زخرفة خفيفة (نقاط ذهبية) لخلفيات البانرات
const dots = 'radial-gradient(rgba(212,175,55,0.22) 1px, transparent 1.4px)';

const CtaButton = ({ to, children, variant = 'light', testId }) => {
  const className = cn(
    'group inline-flex items-center gap-2 h-12 md:h-14 px-7 md:px-9 rounded-full font-cairo font-bold text-base md:text-lg shadow-xl transition-all duration-300 hover:-translate-y-0.5',
    variant === 'light' && 'bg-white text-burgundy-600 hover:bg-brand-gold hover:text-brand-black',
    variant === 'dark' && 'bg-burgundy-500 text-white hover:bg-burgundy-600 shadow-burgundy-500/30',
    variant === 'gold' && 'bg-brand-gold text-brand-black hover:bg-white'
  );
  const content = (
    <>
      {children}
      <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
    </>
  );

  // رابط خارجي (من لوحة التحكم) بيفتح كـ <a> عادي، والداخلي عبر الراوتر
  if (/^https?:\/\//i.test(to)) {
    return (
      <a href={to} data-testid={testId} className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={className}>
      {content}
    </Link>
  );
};

const SlideShell = ({ children, className, style }) => (
  <div className={cn('relative min-w-0 shrink-0 grow-0 basis-full h-[560px] sm:h-[500px] md:h-[560px] overflow-hidden', className)} style={style}>
    {children}
  </div>
);

// شريحة من لوحة التحكم: صورة + (اختياري) عنوان/وصف/زر. لو مفيش نص بتظهر الصورة لحالها.
const CustomSlide = ({ slide, priority }) => {
  const hasText = Boolean(slide.eyebrow || slide.title || slide.subtitle || (slide.button_text && slide.button_link));
  return (
    <SlideShell>
      <img
        src={slide.image_url}
        alt={slide.title || ''}
        className="absolute inset-0 w-full h-full object-cover"
        loading={priority ? 'eager' : 'lazy'}
        draggable={false}
      />
      {hasText && (
        <>
          <div className="absolute inset-0 bg-gradient-to-l from-black/10 via-black/25 to-black/70" />
          <div className="relative h-full container mx-auto px-4 sm:px-6 md:px-20 lg:px-24 max-w-[1600px] flex items-center pb-20 md:pb-0">
            <div className="max-w-2xl text-white hero-slide-in">
              {slide.eyebrow && (
                <p className="inline-flex items-center gap-2 text-xs md:text-sm tracking-[0.25em] text-brand-gold font-bold mb-4">
                  <span className="w-8 h-px bg-brand-gold" /> {slide.eyebrow}
                </p>
              )}
              {slide.title && (
                <h2 className="text-5xl md:text-7xl font-tajawal font-extrabold mb-5 leading-[1.25] drop-shadow-lg">{slide.title}</h2>
              )}
              {slide.subtitle && (
                <p className="text-lg md:text-2xl font-cairo mb-8 leading-relaxed text-white/90">{slide.subtitle}</p>
              )}
              {slide.button_text && slide.button_link && <CtaButton to={slide.button_link}>{slide.button_text}</CtaButton>}
            </div>
          </div>
        </>
      )}
    </SlideShell>
  );
};

/**
 * سلايدر الهيرو: شرائح لوحة التحكم (/admin/sliders) لو فيه، وإلا الشرائح الافتراضية
 * (صورة الماركة + بانر الخصومات + بانر الدفع عند الاستلام). صورة الماركة + بانر الخصومات + بانر الدفع عند الاستلام.
 * بيقلب لوحده كل 6 ثواني، وبيقف لما الماوس عليه.
 */
const HeroCarousel = ({ siteName, maxDiscount, saleCount, deliveryFee }) => {
  // شريحة الخصومات بتظهر بس لو فيه خصومات فعلية
  const hasSale = saleCount > 0 && maxDiscount > 0;
  const [emblaRef, emblaApi] = useEmblaCarousel({ direction: 'rtl', loop: true, duration: 32 });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  // null = لسا بيتحمّل، [] = مفيش شرائح من اللوحة (نعرض الافتراضي)
  const [customSlides, setCustomSlides] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/home/sliders`)
      .then((res) => !cancelled && setCustomSlides(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => !cancelled && setCustomSlides([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelect = useCallback((api) => setSelected(api.selectedScrollSnap()), []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || paused) return;
    const timer = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [emblaApi, paused, selected]);

  const defaultSlides = [
    // 1) صورة الماركة
    <SlideShell key="brand" style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-gradient-to-l from-black/10 via-black/25 to-black/70" />
      <div className="relative h-full container mx-auto px-4 sm:px-6 md:px-20 lg:px-24 max-w-[1600px] flex items-center pb-20 md:pb-0">
        <div className="max-w-2xl text-white hero-slide-in">
          <p className="inline-flex items-center gap-2 text-xs md:text-sm tracking-[0.25em] text-brand-gold font-bold mb-4">
            <span className="w-8 h-px bg-brand-gold" /> تشكيلة الموسم الجديدة
          </p>
          <h1 className="text-5xl md:text-7xl font-tajawal font-extrabold mb-5 leading-[1.25] drop-shadow-lg">{siteName}</h1>
          <p className="text-lg md:text-2xl font-cairo mb-8 leading-relaxed text-white/90">
            اكتشفي أحدث صيحات الموضة بأرقى الخامات وأجمل التصاميم
          </p>
          <CtaButton to="/products" testId={HOME.shopNowBtn}>تسوقي الآن</CtaButton>
        </div>
      </div>
    </SlideShell>,

    // 2) الخصومات
    <SlideShell key="sale" className="bg-gradient-to-l from-burgundy-600 via-burgundy-500 to-[#4a0a10]">
      <div className="absolute inset-0 opacity-70" style={{ backgroundImage: dots, backgroundSize: '22px 22px' }} />
      <div className="absolute -left-24 -top-24 w-[420px] h-[420px] rounded-full border-[40px] border-brand-gold/10" />
      <div className="absolute left-10 md:left-32 top-1/2 -translate-y-1/2 hidden sm:flex items-center justify-center">
        <div className="relative w-56 h-56 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-brand-gold to-[#b8912a] shadow-2xl shadow-black/30 flex flex-col items-center justify-center text-brand-black rotate-[-8deg] hero-float">
          <span className="font-cairo font-bold text-lg md:text-2xl">خصم حتى</span>
          <span className="font-tajawal font-extrabold text-7xl md:text-9xl leading-none">{maxDiscount}%</span>
          <span className="absolute -top-2 -right-2 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white text-burgundy-600 flex items-center justify-center shadow-lg">
            <BadgePercent className="w-7 h-7 md:w-8 md:h-8" />
          </span>
        </div>
      </div>
      <div className="relative h-full container mx-auto px-4 sm:px-6 md:px-20 lg:px-24 max-w-[1600px] flex items-center pb-20 md:pb-0">
        <div className="max-w-xl text-white">
          <p className="inline-block bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-sm font-cairo font-bold text-brand-gold mb-5 ring-1 ring-white/15">
            عروض مميزة
          </p>
          <h2 className="text-4xl md:text-6xl font-tajawal font-extrabold leading-[1.3] mb-4">
            تخفيضات <span className="text-brand-gold">الموسم</span>
            <br />
            بدأت الآن
          </h2>
          <p className="text-base md:text-xl font-cairo text-white/85 mb-8">
            {saleCount} قطعة بأسعار مخفّضة الآن — اختاري قطعتك بسعر أقل
          </p>
          <div className="sm:hidden mb-6 inline-flex items-baseline gap-2 bg-brand-gold text-brand-black rounded-2xl px-5 py-3">
            <span className="font-cairo font-bold">خصم حتى</span>
            <span className="font-tajawal font-extrabold text-4xl">{maxDiscount}%</span>
          </div>
          <div>
            <CtaButton to="/products?on_sale=1&sort=discount" variant="gold">تسوّقي العروض</CtaButton>
          </div>
        </div>
      </div>
    </SlideShell>,

    // 3) الدفع عند الاستلام والمعاينة
    <SlideShell key="cod" className="bg-[#f7efe9]">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(114,47,55,0.10) 1px, transparent 1.4px)', backgroundSize: '20px 20px' }} />
      <div className="absolute -right-32 -bottom-32 w-[480px] h-[480px] rounded-full bg-burgundy-500/[0.06]" />
      <div className="relative h-full container mx-auto px-4 sm:px-6 md:px-20 lg:px-24 max-w-[1600px] flex items-center pb-20 md:pb-0">
        <div className="grid md:grid-cols-2 gap-10 items-center w-full">
          <div>
            <p className="text-sm font-cairo font-bold text-burgundy-500 mb-4 tracking-wide">تسوّقي براحة وثقة</p>
            <h2 className="text-4xl md:text-6xl font-tajawal font-extrabold text-brand-black leading-[1.3] mb-5">
              عايني طلبك
              <br />
              <span className="text-burgundy-500">وبعدين ادفعي</span>
            </h2>
            <p className="text-base md:text-xl font-cairo text-gray-600 mb-8 max-w-md">
              الدفع عند الاستلام مع خدمة المعاينة قبل الدفع، والتوصيل لجميع مناطق المملكة.
            </p>
            <CtaButton to="/products" variant="dark">ابدئي التسوق</CtaButton>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-4 max-w-md mr-auto">
            {[
              { icon: Wallet, title: 'الدفع عند الاستلام', text: 'بدون بطاقات' },
              { icon: Eye, title: 'معاينة قبل الدفع', text: 'شوفي قبل ما تدفعي' },
              { icon: Truck, title: 'توصيل لكل المملكة', text: `${deliveryFee} د.أ فقط` },
              { icon: BadgePercent, title: 'أسعار منافسة', text: 'وعروض مستمرة' },
            ].map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className={cn('rounded-3xl bg-white p-5 shadow-lg shadow-burgundy-500/5 ring-1 ring-burgundy-500/10', i % 2 === 1 && 'translate-y-6')}
              >
                <span className="w-12 h-12 rounded-2xl bg-burgundy-500 text-brand-gold flex items-center justify-center mb-3">
                  <Icon className="w-6 h-6" />
                </span>
                <p className="font-tajawal font-bold text-brand-black">{title}</p>
                <p className="font-cairo text-sm text-gray-500">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideShell>,
  ].filter((slide) => hasSale || slide.key !== 'sale');

  const hasCustom = customSlides?.length > 0;
  // شرائح اللوحة أولاً، وبعدها الشرائح الثابتة (الخصومات + الدفع عند الاستلام) لأنها مش صور
  const slides = hasCustom
    ? [
        ...customSlides.map((slide, i) => <CustomSlide key={`custom-${slide.id}`} slide={slide} priority={i === 0} />),
        ...defaultSlides.filter((slide) => slide.key !== 'brand'),
      ]
    : defaultSlides;

  // لما عدد الشرائح يتغيّر (بعد وصول شرائح اللوحة) لازم Embla يعيد حساب الشرائح
  useEffect(() => {
    emblaApi?.reInit();
    setSelected(0);
  }, [emblaApi, slides.length]);

  const showControls = customSlides !== null && slides.length > 1;

  return (
    <section
      data-testid={HOME.heroSection}
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="العروض الرئيسية"
    >
      {customSlides === null ? (
        // مكان محجوز لحد ما نعرف نعرض شرائح اللوحة ولا الافتراضي (عشان ما يحصلش وميض)
        <div className="h-[560px] sm:h-[500px] md:h-[560px] bg-gradient-to-l from-burgundy-600 via-burgundy-500 to-[#4a0a10]" aria-busy="true" />
      ) : (
        <div ref={emblaRef} className="overflow-hidden" dir="rtl">
          <div className="flex touch-pan-y">{slides}</div>
        </div>
      )}

      {/* الأسهم */}
      {showControls && (
        <>
          <button
            type="button"
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="السابق"
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/80 backdrop-blur text-brand-black shadow-lg items-center justify-center hover:bg-white transition"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => emblaApi?.scrollNext()}
            aria-label="التالي"
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/80 backdrop-blur text-brand-black shadow-lg items-center justify-center hover:bg-white transition"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        </>
      )}

      {/* النقاط + شريط التقدّم */}
      {showControls && (
        <div className="absolute bottom-12 md:bottom-16 inset-x-0 flex justify-center gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`الشريحة ${i + 1}`}
              aria-current={i === selected}
              className={cn(
                'relative h-2 rounded-full overflow-hidden transition-all duration-300 bg-white/50 ring-1 ring-black/5',
                i === selected ? 'w-10' : 'w-2 hover:bg-white/80'
              )}
            >
              {i === selected && (
                <span
                  key={`${selected}-${paused}`}
                  className={cn('absolute inset-y-0 right-0 bg-brand-gold', !paused && 'hero-progress')}
                  style={{ animationDuration: `${AUTOPLAY_MS}ms`, width: paused ? '100%' : undefined }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;
